import { Ionicons } from '@expo/vector-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

export type LevelInfo = {
  level: number;
  completed: number;
  required_current: number;
  required_next: number;
  title: string;
};

type BadgeSize = 'sm' | 'md' | 'lg';

type Props = {
  info: LevelInfo;
  size?: BadgeSize;
  /** Επιπλέον stats για το popup */
  memberSince?: string;
  avgRating?: number | null;
};

/**
 * Ο τίτλος επιπέδου (info.title) έρχεται σταθερά στα Αγγλικά από τη βάση
 * (π.χ. "Bronze", "Gold") — δεν ακολουθεί τη γλώσσα της εφαρμογής. Εδώ τον
 * ξαναϋπολογίζουμε client-side από το level με τα ήδη μεταφρασμένα tier
 * labels (ίδια όρια: 1–9, 10–24, 25–49, 50–74, 75–99, 100).
 */
export function levelTierKey(level: number): 'tier_legend' | 'tier_expert' | 'tier_veteran' | 'tier_experienced' | 'tier_active' | 'tier_new' {
  if (level >= 100) return 'tier_legend';
  if (level >= 75)  return 'tier_expert';
  if (level >= 50)  return 'tier_veteran';
  if (level >= 25)  return 'tier_experienced';
  if (level >= 10)  return 'tier_active';
  return 'tier_new';
}

export function levelIcon(level: number): keyof typeof Ionicons.glyphMap {
  if (level >= 100) return 'flame';
  if (level >= 75)  return 'ribbon';
  if (level >= 50)  return 'trophy';
  if (level >= 25)  return 'shield-checkmark';
  if (level >= 10)  return 'flash';
  return 'leaf';
}

export function levelColor(level: number): string {
  if (level >= 100) return '#7C3AED';
  if (level >= 75)  return '#3B82F6';
  if (level >= 50)  return '#F59E0B';
  if (level >= 25)  return '#C0C0C0';
  if (level >= 10)  return '#CD7F32';
  return '#9CA3AF';
}

function levelBg(level: number): string {
  if (level >= 100) return '#EDE9FE';
  if (level >= 75)  return '#DBEAFE';
  if (level >= 50)  return '#FEF3C7';
  if (level >= 25)  return '#F3F4F6';
  if (level >= 10)  return '#FDF3E7';
  return '#F3F4F6';
}

const SIZE: Record<BadgeSize, { pill: number; font: number; icon: number }> = {
  sm: { pill: 18, font: 10, icon: 0  },
  md: { pill: 24, font: 12, icon: 12 },
  lg: { pill: 30, font: 14, icon: 14 },
};

export function LevelBadge({ info, size = 'md', memberSince, avgRating }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [open, setOpen] = useState(false);
  const insets = useSafeAreaInsets();

  const TIERS: { label: string; range: string; color: string; icon: keyof typeof Ionicons.glyphMap }[] = [
    { label: t('levelBadge.tier_new'),        range: '1–9',    color: '#9CA3AF', icon: 'leaf' },
    { label: t('levelBadge.tier_active'),      range: '10–24',  color: '#CD7F32', icon: 'flash' },
    { label: t('levelBadge.tier_experienced'), range: '25–49',  color: '#C0C0C0', icon: 'shield-checkmark' },
    { label: t('levelBadge.tier_veteran'),     range: '50–74',  color: '#F59E0B', icon: 'trophy' },
    { label: t('levelBadge.tier_expert'),      range: '75–99',  color: '#3B82F6', icon: 'ribbon' },
    { label: t('levelBadge.tier_legend'),      range: '100',    color: '#7C3AED', icon: 'flame' },
  ];
  const s = SIZE[size];
  const color = levelColor(info.level);
  const bg = levelBg(info.level);
  const title = t(`levelBadge.${levelTierKey(info.level)}`);

  const progress = info.required_next > info.required_current
    ? Math.min(1, (info.completed - info.required_current) / (info.required_next - info.required_current))
    : 1;

  return (
    <>
      <Pressable
        style={[styles.badge, { backgroundColor: bg, borderColor: color, height: s.pill, paddingHorizontal: s.pill * 0.45 }]}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('levelBadge.accessibilityLabel', { level: info.level, title })}
      >
        {size !== 'sm' && (
          <Ionicons name={levelIcon(info.level)} size={s.icon} color={color} />
        )}
        <Text style={[styles.badgeText, { color, fontSize: s.font }]}>
          {size !== 'sm' ? title : String(info.level)}
        </Text>
      </Pressable>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.overlay} onPress={() => setOpen(false)}>
          <Pressable
            style={[styles.popup, { paddingBottom: insets.bottom + spacing.lg }]}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <View style={[styles.popupHeader, { backgroundColor: bg }]}>
              <View style={styles.popupLevelRow}>
                <Ionicons name={levelIcon(info.level)} size={20} color={color} />
                <Text style={[styles.popupLevel, { color }]}>{t('home.level', { level: info.level })}</Text>
              </View>
              <Text style={[styles.popupTitle, { color }]}>{title}</Text>
              <Pressable style={styles.popupClose} onPress={() => setOpen(false)} hitSlop={12}>
                <Ionicons name="close" size={20} color={color} />
              </Pressable>
            </View>

            <View style={styles.popupBody}>
              {/* Progress bar */}
              {info.level < 100 && (
                <View style={styles.progressSection}>
                  <View style={styles.progressLabels}>
                    <Text style={styles.progressLabel}>{t('common.progress')}</Text>
                    <Text style={styles.progressLabel}>
                      {t('levelBadge.progressLabel', { completed: info.completed, required: info.required_next })}
                    </Text>
                  </View>
                  <View style={styles.progressTrack}>
                    <View style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` as any, backgroundColor: color }]} />
                  </View>
                  <Text style={styles.progressSub}>
                    {t('levelBadge.toNextLevel', { count: Math.max(0, info.required_next - info.completed), next: info.level + 1 })}
                  </Text>
                </View>
              )}

              {/* Stats */}
              <View style={styles.statsGrid}>
                <StatCell label={t('portfolio.stat_completed')} value={String(info.completed)} icon="checkmark-circle-outline" styles={styles} />
                {avgRating != null && (
                  <StatCell label={t('levelBadge.avgRating')} value={avgRating.toFixed(1) + ' ★'} icon="star-outline" styles={styles} />
                )}
                {memberSince && (
                  <StatCell label={t('user.memberSince')} value={memberSince} icon="calendar-outline" styles={styles} />
                )}
              </View>

              {/* Tier legend */}
              <View style={styles.tiers}>
                {TIERS.map((t) => (
                  <View key={t.label} style={styles.tierRow}>
                    <Ionicons name={t.icon} size={14} color={t.color} />
                    <Text style={[styles.tierLabel, { color: t.color }]}>{t.label}</Text>
                    <Text style={styles.tierRange}>{t.range}</Text>
                  </View>
                ))}
              </View>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function StatCell({ label, value, icon, styles }: { label: string; value: string; icon: keyof typeof Ionicons.glyphMap; styles: ReturnType<typeof makeStyles> }) {
  const { colors } = useTheme();
  return (
    <View style={styles.statCell}>
      <Ionicons name={icon} size={18} color={colors.textMuted} />
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      borderRadius: radius.full,
      borderWidth: 1.5,
    },
    badgeText: {
      fontWeight: '700',
      letterSpacing: 0.3,
    },
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'flex-end',
    },
    popup: {
      backgroundColor: colors.background,
      borderTopLeftRadius: radius.xl,
      borderTopRightRadius: radius.xl,
      overflow: 'hidden',
      ...shadow.card,
    },
    popupHeader: {
      padding: spacing.lg,
      paddingTop: spacing.xl,
      alignItems: 'center',
      gap: spacing.xs,
      position: 'relative',
    },
    popupLevelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    popupLevel: {
      fontSize: fontSize.xxl,
      fontWeight: '800',
    },
    popupTitle: {
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    popupClose: {
      position: 'absolute',
      top: spacing.md,
      right: spacing.md,
    },
    popupBody: {
      padding: spacing.lg,
      gap: spacing.lg,
    },
    progressSection: {
      gap: spacing.xs,
    },
    progressLabels: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    progressLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    progressTrack: {
      height: 8,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      borderRadius: radius.full,
    },
    progressSub: {
      color: colors.textMuted,
      fontSize: 12,
      textAlign: 'right',
    },
    statsGrid: {
      flexDirection: 'row',
      gap: spacing.sm,
    },
    statCell: {
      flex: 1,
      alignItems: 'center',
      gap: 4,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.sm,
    },
    statValue: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    statLabel: {
      color: colors.textMuted,
      fontSize: 11,
      textAlign: 'center',
    },
    tiers: {
      gap: spacing.xs,
    },
    tierRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    tierDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
    },
    tierLabel: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tierRange: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
  });
}
